import {Injectable} from "@angular/core";
import {OAuthService} from "angular-oauth2-oidc";
import {Router} from "@angular/router";
import {Location} from "@angular/common";
import {BehaviorSubject, lastValueFrom, of} from "rxjs";
import {filter, first, map} from "rxjs/operators";
import {LocalLanguageService} from "../../services/local-language.service";
import {environment} from "../../../../environments/environment";
import {FormatConfigService} from 'app/core/services/format-config.service';
import {log} from "../../../log/log";

@Injectable()
export class CapiLoginService {

  userInfo: any;

  private state = new BehaviorSubject<State>(State.NO_INITIALIZED);
  state$ = this.state.asObservable();

  constructor(private oauth: OAuthService,
              private router: Router,
              private location: Location,
              private localLanguageService: LocalLanguageService,
              protected appformatConfigService: FormatConfigService) {
    oauth.redirectUri = window.location.origin + location.prepareExternalUrl("/");
    this.state$.subscribe(state => this.stateChanged(state));
  }

  private stateChanged(state: State) {
    switch (state) {
      case State.NO_INITIALIZED:
        this.oauth.loadDiscoveryDocument().then(() => {
          this.state.next(State.DISCOVERY_DOCUMENT_RECEIVED);
        });
        break;
      case State.DISCOVERY_DOCUMENT_RECEIVED:
        this.oauth.tryLoginCodeFlow().then(() => {
          if (this.oauth.hasValidAccessToken()) {
            if (!!this.oauth.state) {
              let stateUrl = this.oauth.state;
              if (!stateUrl.startsWith('/')) {
                stateUrl = decodeURIComponent(stateUrl);
              }
              this.router.navigateByUrl(stateUrl);
            }
            log.debug("CapiLoginService: hasValidAccessToken=TRUE");
            this.userInfo = this.oauth.getIdentityClaims();
            this.oauth.setupAutomaticSilentRefresh();
            this.state.next(State.IN);
          } else {
            this.state.next(State.OUT);
          }
        }, () => {
          this.state.next(State.OUT);
        });
        break;
      default:
      // Do nothing
    }
  }

  requireLogin(path?: string) {
    const loginICPRequired = <any>this.appformatConfigService.parameterConfig?.parameters.ZUGELASSENE_BENUTZERKATEGORIEN === 'REGISTRIERT';
    if (environment.deploymentMode === 'PUBLIC' && !loginICPRequired) {
      return of(false);
    } else {
      const requireLogin = this.state$.pipe(
        filter(state => state === State.IN || state === State.OUT),
        first(),
        map(state => {
          if (state === State.OUT) {
            this.login(path);
          }
          return true;
        }));
      return lastValueFrom(requireLogin);
    }
  }

  login(path = this.location.path(false)): Promise<true> {
    if (!this.loginAllowed()) {
      return;
    }
    this.oauth.initCodeFlow(path, this.getParams());
    return new Promise<true>((resolve, reject) => {
    });
  }

  loginAllowed(): boolean {
    const anonym = <any>this.appformatConfigService.parameterConfig?.parameters.ZUGELASSENE_BENUTZERKATEGORIEN === 'ANONYM';
    const deploymentModePublic = environment.deploymentMode === 'PUBLIC';
    if (deploymentModePublic) {
      return !anonym;
    }
    return true;
  }

  private getParams() {
    if (environment.openIdConnect.idpHint) {
      return {
        'ui_locales': this.localLanguageService.getLocalLanguage(),
        'kc_idp_hint': environment.openIdConnect.idpHint
      };
    } else {
      return {'ui_locales': this.localLanguageService.getLocalLanguage()};
    }
  }

  logout() {
    this.oauth.logOut();
    this.userInfo = null;
  }

  get userLoggedIn() {
    return this.oauth.hasValidAccessToken();
  }

}

enum State {
  NO_INITIALIZED,
  DISCOVERY_DOCUMENT_RECEIVED,
  IN,
  OUT
}


